import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  History,
  Users,
  Calendar,
  DollarSign,
  Search,
  Eye,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Download,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const RenterHistoryManager = ({ salonId }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [approvedApplications, setApprovedApplications] = useState([]);
  const [activeRenters, setActiveRenters] = useState([]);
  const [renterHistory, setRenterHistory] = useState([]);
  const [selectedRenter, setSelectedRenter] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchRenterData();
  }, [salonId]);

  const fetchRenterData = async () => {
    if (!salonId) return;
    try {
      const response = await axios.get(`${API}/booths/salon/${salonId}/renters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApprovedApplications(response.data.approved_applications || []);
      setActiveRenters(response.data.active_renters || []);
      setRenterHistory(response.data.renter_history || []);
    } catch (error) {
      console.error("Failed to fetch renter data:", error);
    } finally {
      setLoading(false);
    }
  };

  const viewRenterProfile = async (userId) => {
    try {
      const response = await axios.get(`${API}/booths/renter/${userId}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedRenter(response.data);
      setShowDetailsDialog(true);
    } catch (error) {
      toast.error("Failed to load renter profile");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const calculateTotalPaid = (rentals) => {
    if (!rentals || rentals.length === 0) return 0;
    return rentals.reduce((sum, r) => sum + (r.total_paid || 0), 0);
  };

  const filteredHistory = [...approvedApplications, ...renterHistory].filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.full_name?.toLowerCase().includes(query) ||
      item.renter_name?.toLowerCase().includes(query) ||
      item.email?.toLowerCase().includes(query) ||
      item.renter_email?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <Card className="bg-stone-800/50 border-stone-700">
        <CardContent className="py-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-500" />
          <p className="text-stone-400 mt-2">Loading renter history...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-400 text-sm">Approved Applications</p>
                <p className="text-2xl font-bold text-green-400">{approvedApplications.length}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-400 text-sm">Active Renters</p>
                <p className="text-2xl font-bold text-amber-400">{activeRenters.length}</p>
              </div>
              <Users className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-400 text-sm">Past Renters</p>
                <p className="text-2xl font-bold text-stone-300">{renterHistory.length}</p>
              </div>
              <History className="w-8 h-8 text-stone-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Card className="bg-stone-800/50 border-stone-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5 text-amber-500" />
              Renter History
            </CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search renters..."
                className="pl-9 bg-stone-700 border-stone-600 text-white w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredHistory.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-12 h-12 mx-auto text-stone-600 mb-4" />
              <p className="text-stone-400">No renter history yet</p>
              <p className="text-stone-500 text-sm">Approved applications and past renters will appear here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredHistory.map((item, index) => {
                const isApproved = item.status === "approved";
                const isCompleted = item.status === "completed";
                const isTerminated = item.status === "terminated";
                const itemId = item.application_id || item.assignment_id || `item-${index}`;
                const isExpanded = expandedId === itemId;

                return (
                  <div
                    key={itemId}
                    className="border border-stone-700 rounded-lg overflow-hidden"
                  >
                    {/* Header */}
                    <div
                      className="p-4 bg-stone-700/30 flex items-center justify-between cursor-pointer hover:bg-stone-700/50 transition-colors"
                      onClick={() => setExpandedId(isExpanded ? null : itemId)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-stone-700 flex items-center justify-center">
                          <span className="text-xl font-bold text-amber-400">
                            {(item.full_name || item.renter_name)?.[0]?.toUpperCase() || "?"}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-white font-semibold">
                              {item.full_name || item.renter_name}
                            </h3>
                            <Badge className={`text-xs ${
                              isApproved ? "bg-green-500" :
                              isCompleted ? "bg-blue-500" :
                              isTerminated ? "bg-red-500" :
                              "bg-stone-500"
                            }`}>
                              {item.status?.replace("_", " ").toUpperCase() || "UNKNOWN"}
                            </Badge>
                          </div>
                          <p className="text-stone-400 text-sm">
                            {item.professional_type?.replace("_", " ") || item.specialty || "Professional"} •
                            {item.years_experience || 0} years exp
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-stone-400 text-xs">
                            {isApproved ? "Approved" : isCompleted ? "Ended" : "Date"}
                          </p>
                          <p className="text-stone-300 text-sm">
                            {formatDate(item.submitted_at || item.start_date || item.created_at)}
                          </p>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-stone-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-stone-500" />
                        )}
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="p-4 border-t border-stone-700 bg-stone-800/30">
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Contact Info */}
                          <div className="space-y-3">
                            <h4 className="text-white font-medium flex items-center gap-2">
                              <Users className="w-4 h-4 text-amber-500" />
                              Contact Information
                            </h4>
                            <div className="space-y-2 text-sm">
                              <p className="flex items-center gap-2 text-stone-300">
                                <Mail className="w-4 h-4 text-stone-500" />
                                {item.email || item.renter_email || "N/A"}
                              </p>
                              <p className="flex items-center gap-2 text-stone-300">
                                <Phone className="w-4 h-4 text-stone-500" />
                                {item.phone || item.renter_phone || "N/A"}
                              </p>
                            </div>
                          </div>

                          {/* Rental Info */}
                          <div className="space-y-3">
                            <h4 className="text-white font-medium flex items-center gap-2">
                              <Briefcase className="w-4 h-4 text-amber-500" />
                              Rental Details
                            </h4>
                            <div className="space-y-2 text-sm">
                              {item.booth_number && (
                                <p className="text-stone-300">
                                  <span className="text-stone-500">Booth:</span> {item.booth_number}
                                </p>
                              )}
                              {item.rent_amount && (
                                <p className="text-stone-300">
                                  <span className="text-stone-500">Rent:</span> ${item.rent_amount}/month
                                </p>
                              )}
                              {item.start_date && (
                                <p className="text-stone-300">
                                  <span className="text-stone-500">Start Date:</span> {formatDate(item.start_date)}
                                </p>
                              )}
                              {item.end_date && (
                                <p className="text-stone-300">
                                  <span className="text-stone-500">End Date:</span> {formatDate(item.end_date)}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Application Notes */}
                          {(item.about_me || item.why_interested) && (
                            <div className="md:col-span-2 space-y-2">
                              {item.about_me && (
                                <div>
                                  <h5 className="text-white font-medium text-sm">About</h5>
                                  <p className="text-stone-400 text-sm bg-stone-700/30 p-3 rounded mt-1">
                                    {item.about_me}
                                  </p>
                                </div>
                              )}
                              {item.why_interested && (
                                <div>
                                  <h5 className="text-white font-medium text-sm">Why Interested</h5>
                                  <p className="text-stone-400 text-sm bg-stone-700/30 p-3 rounded mt-1">
                                    {item.why_interested}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Review History */}
                          {item.review_notes?.length > 0 && (
                            <div className="md:col-span-2 space-y-2">
                              <h4 className="text-white font-medium flex items-center gap-2">
                                <History className="w-4 h-4 text-amber-500" />
                                Review History
                              </h4>
                              <div className="space-y-2">
                                {item.review_notes.map((note, idx) => (
                                  <div key={idx} className="bg-stone-700/30 p-3 rounded text-sm border-l-2 border-amber-500">
                                    <div className="flex justify-between items-start mb-1">
                                      <Badge className={
                                        note.status === "approved" ? "bg-green-500" :
                                        note.status === "denied" ? "bg-red-500" :
                                        "bg-blue-500"
                                      }>
                                        {note.status?.toUpperCase()}
                                      </Badge>
                                      <span className="text-stone-500 text-xs">{formatDate(note.reviewed_at)}</span>
                                    </div>
                                    {note.notes && <p className="text-stone-300">{note.notes}</p>}
                                    {note.conditions && (
                                      <p className="text-amber-400 mt-1">
                                        <span className="text-stone-500">Conditions:</span> {note.conditions}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-stone-700">
                          {item.renter_id && (
                            <Button
                              variant="outline"
                              className="border-stone-600"
                              onClick={() => viewRenterProfile(item.renter_id)}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View Full Profile
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Renter Profile Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              Renter Profile - {selectedRenter?.user?.name || selectedRenter?.profile?.business_name || "Unknown"}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRenter && (
            <div className="space-y-4 mt-4">
              {/* User Info */}
              {selectedRenter.user && (
                <div className="bg-stone-700/30 p-4 rounded-lg">
                  <h4 className="text-white font-medium mb-3">Contact Information</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <p className="text-stone-300">
                      <span className="text-stone-500">Name:</span> {selectedRenter.user.name}
                    </p>
                    <p className="text-stone-300">
                      <span className="text-stone-500">Email:</span> {selectedRenter.user.email}
                    </p>
                    <p className="text-stone-300">
                      <span className="text-stone-500">Phone:</span> {selectedRenter.user.phone || "N/A"}
                    </p>
                    <p className="text-stone-300">
                      <span className="text-stone-500">Member Since:</span> {formatDate(selectedRenter.user.created_at)}
                    </p>
                  </div>
                </div>
              )}

              {/* Profile Info */}
              {selectedRenter.profile && (
                <div className="bg-stone-700/30 p-4 rounded-lg">
                  <h4 className="text-white font-medium mb-3">Professional Profile</h4>
                  <div className="space-y-2 text-sm">
                    <p className="text-stone-300">
                      <span className="text-stone-500">Type:</span> {selectedRenter.profile.professional_type?.replace("_", " ")}
                    </p>
                    <p className="text-stone-300">
                      <span className="text-stone-500">Experience:</span> {selectedRenter.profile.experience_years || 0} years
                    </p>
                    {selectedRenter.profile.bio && (
                      <p className="text-stone-300">
                        <span className="text-stone-500">Bio:</span> {selectedRenter.profile.bio}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Application History */}
              {selectedRenter.applications?.length > 0 && (
                <div className="bg-stone-700/30 p-4 rounded-lg">
                  <h4 className="text-white font-medium mb-3">Application History ({selectedRenter.applications.length})</h4>
                  <div className="space-y-2">
                    {selectedRenter.applications.map((app, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm">
                        <span className="text-stone-300">{formatDate(app.submitted_at)}</span>
                        <Badge className={
                          app.status === "approved" ? "bg-green-500" :
                          app.status === "denied" ? "bg-red-500" :
                          "bg-yellow-500"
                        }>
                          {app.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rental History */}
              {selectedRenter.rental_history?.length > 0 && (
                <div className="bg-stone-700/30 p-4 rounded-lg">
                  <h4 className="text-white font-medium mb-3">Rental History ({selectedRenter.rental_history.length})</h4>
                  <div className="space-y-3">
                    {selectedRenter.rental_history.map((rental, idx) => (
                      <div key={idx} className="border border-stone-600 rounded p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-white font-medium">Booth {rental.booth_number || "N/A"}</p>
                            <p className="text-stone-400 text-sm">
                              {formatDate(rental.start_date)} - {rental.end_date ? formatDate(rental.end_date) : "Present"}
                            </p>
                          </div>
                          <Badge className={rental.status === "active" ? "bg-green-500" : "bg-stone-500"}>
                            {rental.status}
                          </Badge>
                        </div>
                        {rental.total_paid && (
                          <p className="text-amber-400 text-sm mt-2">
                            Total Paid: ${rental.total_paid.toLocaleString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RenterHistoryManager;
